import type { SuccessEnvelope as Envelope, Deleted } from '../../../../core/interfaces/http/response.interface';
import type { AuthIdentity } from '../../../../core/interfaces/backend/api_auth/api_auth.interface';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, catchError, map, of, shareReplay, switchMap, throwError, finalize, from } from 'rxjs';
import { environment } from '../../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiAuthService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  readonly loggingOut = signal(false);
  readonly identity = signal<AuthIdentity | null>(null);
  readonly sessionChecked = signal(false);
  readonly loginError = signal<string | null>(null);
  readonly baseUrl = environment.backendUrl.replace(/\/$/, '');
  private readonly endpoint = this.baseUrl + '/api/public/api-auth';
  private refreshing?: Observable<AuthIdentity>;
  private restoring?: Observable<AuthIdentity>;

  private readonly tokenKey = 'mads.accessToken';
  private readonly storageType = environment.tokenStorage as 'localStorage' | 'sessionStorage';

  private get storage(): Storage | undefined {
    return this.document.defaultView?.[this.storageType];
  }

  private saveIdentity(identity: AuthIdentity): AuthIdentity {
    if (this.loggingOut()) return identity;
    const win = this.document.defaultView;
    const other = this.storageType === 'localStorage' ? win?.sessionStorage : win?.localStorage;
    other?.removeItem(this.tokenKey);
    this.storage?.setItem(this.tokenKey, identity.accessToken);
    this.identity.set(identity);
    this.sessionChecked.set(true);
    return identity;
  }

  accessToken(): string | null {
    return this.storage?.getItem(this.tokenKey) ?? null;
  }

  /** Expiration is only a renewal hint. The backend still verifies the token. */
  needsRefresh(): boolean {
    const token = this.accessToken();
    if (!token) return false;
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
      return typeof claims.exp !== 'number' || claims.exp * 1000 <= Date.now() + 30_000;
    } catch {
      return true;
    }
  }

  clear(): void {
    this.document.defaultView?.localStorage.removeItem(this.tokenKey);
    this.document.defaultView?.sessionStorage.removeItem(this.tokenKey);
    this.identity.set(null);
    this.sessionChecked.set(true);
  }

  login(): void {
    const origin = this.document.defaultView?.location.origin;
    if (!origin) return;
    this.loginError.set(null);
    this.http.get(this.endpoint + '/availability').subscribe({
      next: () => this.document.defaultView?.location.assign(this.endpoint + '/login?frontend_origin=' + encodeURIComponent(origin)),
      error: () => this.loginError.set('El servicio de autenticación no está disponible. Intente nuevamente.')
    });
  }

  session(): Observable<AuthIdentity> {
    return this.http.get<Envelope<AuthIdentity>>(this.endpoint + '/session', { withCredentials: true }).pipe(
      map(result => this.saveIdentity(result.data))
    );
  }

  refresh(): Observable<AuthIdentity> {
    if (!this.refreshing) {
      this.refreshing = this.http.post<Envelope<AuthIdentity>>(this.endpoint + '/refresh', {}, {
        withCredentials: true, headers: { 'X-CSRF-Protection': '1' }
      }).pipe(
        map(result => this.saveIdentity(result.data)),
        finalize(() => { this.refreshing = undefined; }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.refreshing;
  }

  restore(): Observable<AuthIdentity> {
    if (!this.restoring) {
      const restore = this.needsRefresh() ? this.refresh() : this.session().pipe(
        catchError((error: HttpErrorResponse) => error.status === 401 ? this.refresh() : throwError(() => error))
      );
      this.restoring = restore.pipe(
        catchError(error => {
          if (error.status === 401 || error.status === 403) this.clear();
          return throwError(() => error);
        }),
        finalize(() => { this.restoring = undefined; }),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.restoring;
  }

  logout(): Observable<void> {
    this.loggingOut.set(true);
    this.clear();
    return this.http.post<Envelope<{ logoutUrl: string }>>(this.endpoint + '/logout', {}, {
      withCredentials: true, headers: { 'X-CSRF-Protection': '1' }
    }).pipe(map(result => {
      this.identity.set(null);
      this.document.defaultView?.location.assign(result.data.logoutUrl);
    }), catchError(error => from(this.router.navigateByUrl('/inicio-sesion')).pipe(
      switchMap(() => {
        this.loggingOut.set(false);
        return throwError(() => error);
      })
    )));
  }
}

/** Actualiza el encabezado también al recargar la portada pública con una cookie vigente. */
export const publicSessionGuard: CanActivateFn = () => {
  const auth = inject(ApiAuthService);
  return auth.restore().pipe(map(() => true), catchError(() => of(true)));
};

/** La pantalla de ingreso solo corresponde a visitantes sin sesión válida. */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(ApiAuthService);
  const router = inject(Router);
  return auth.restore().pipe(
    map(identity => router.parseUrl(identity.nextRoute ?? '/registro-inicial')),
    catchError(() => of(true))
  );
};

export const authenticatedGuard: CanActivateFn = (route, state) => {
  const auth = inject(ApiAuthService);
  const router = inject(Router);
  const hadToken = auth.accessToken() !== null;
  return auth.restore().pipe(
    map(identity => {
      const target = identity.nextRoute ?? '/registro-inicial';
      if (target !== '/dashboard' && state.url.split('?')[0] !== target) return router.parseUrl(target);
      const path = route.routeConfig?.path ?? '';
      if (path === 'registro-inicial' && identity.userType === 'institutional') return router.parseUrl('/dashboard');
      const permissions = identity.permissions ?? [];
      if (state.url.split('?')[0].startsWith('/administracion') && !permissions.includes('administrar_sistema')) return router.parseUrl('/dashboard');
      if (path.startsWith('psa/') || path.startsWith('instrumento/')) {
        const projectId = route.queryParamMap.get('projectId');
        const consult = route.queryParamMap.get('mode') === 'consult';
        const edit = path !== 'instrumento/proyectos';
        const allowed = edit ? projectId ? identity.dashboard?.projects.some(p => p.id === projectId && (consult ? p.canConsult : p.canEdit))
          : permissions.includes('crear_proyectos') : permissions.includes('consultar_proyectos');
        if (!allowed) return router.parseUrl('/dashboard');
      }
      return true;
    }),
    catchError((error: HttpErrorResponse) => of(router.createUrlTree(['/inicio-sesion'], {
      queryParams: error.status === 503 ? {auth_error: 'unavailable'} : hadToken && error.status === 401 ? {session_expired: '1'} : {}
    })))
  );
};

export const backendSessionInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(ApiAuthService);
  const router = inject(Router);
  const backend = new URL(auth.baseUrl, inject(DOCUMENT).baseURI);
  const url = new URL(request.url, inject(DOCUMENT).baseURI);
  const profileEndpoint = /^\/api\/public\/api-auth\/(profile|catalogs|nit-digit|project-catalogs)$/.test(url.pathname);
  if (url.origin !== backend.origin || (!url.pathname.startsWith('/pvt/') && !profileEndpoint)) return next(request);
  const securedRequest = () => {
    const token = auth.accessToken();
    return request.clone({
      withCredentials: true,
      setHeaders: { 'X-CSRF-Protection': '1', ...(token ? { Authorization: 'Bearer ' + token } : {}) }
    });
  };
  const renew = () => auth.refresh().pipe(catchError(failure => {
    if (failure.status === 401 || failure.status === 403) auth.clear();
    void router.navigate(['/inicio-sesion'], { queryParams: failure.status === 503 ? {auth_error: 'unavailable'} : {session_expired: '1'} });
    return throwError(() => failure);
  }));
  const retry = () => next(securedRequest()).pipe(catchError((failure: HttpErrorResponse) => {
    if (failure.status === 401) {
      auth.clear();
      void router.navigate(['/inicio-sesion'], {queryParams: {session_expired: '1'}});
    }
    return throwError(() => failure);
  }));
  const send = () => next(securedRequest()).pipe(catchError((error: HttpErrorResponse) => {
    if (error.status !== 401) return throwError(() => error);
    return renew().pipe(switchMap(retry));
  }));
  // Private requests made after an idle period also renew before sending an expired token.
  return auth.needsRefresh() ? renew().pipe(switchMap(retry)) : send();
};
